// SalesDetailedView.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  Icon,
  Flex,
  Spinner,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Button,
  Heading,
  Collapse,
  useDisclosure,
  SlideFade,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  TableContainer,
  useBreakpointValue,
  IconButton,
  Skeleton,
} from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle,
  FiArrowLeft,
  FiUsers,
  FiShoppingCart,
  FiArrowUp,
  FiArrowDown,
  FiCheckSquare,
  FiGift,
  FiPercent,
} from "react-icons/fi";
import "../../theme/Layout/ChartConfig.js"; // Ensure correct path and extension
import { Bar } from "react-chartjs-2";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../../context/API.js";
import DateRangePicker from "./DateRangePicker.js";
import DateSelector from "./DateSelector.js";
import ChartSelection from "./ChartSelection.js";
import BarLineChart from "./BarLineChart.js";
import moment from "moment";
import CustomersDropDown from "../Loyalty/CutomersDropDown.js";
import StoresDropDown from "./StoresDropDown.js";
import { motion } from "framer-motion";
import Pagination from "../Common/Pagination/Pagination.js";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

// Reusable InfoTile Component
const InfoTile = ({ title, subTitle, value, icon }) => {
  const { isOpen, onToggle } = useDisclosure();

  // Chakra UI color modes
  const tileBg = useColorModeValue("gray.100", "gray.700");
  const tileHoverBg = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");
  const metricBorderColor = useColorModeValue("gray.300", "gray.600");

  return (
    <SlideFade in={true} offsetY="20px" transition={{ duration: 0.5 }}>
      <Box
        bg={tileBg}
        borderRadius="md"
        p={6}
        cursor="pointer"
        _hover={{ bg: tileHoverBg }}
        transition="background 0.3s"
        onClick={onToggle}
      >
        <Flex align="center" justifyContent="space-between">
          <HStack spacing={3}>
            <Icon as={icon} w={8} h={8} color={textColor} />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                {title}
              </Text>
              <Text fontSize="sm" color={subTextColor}>
                {subTitle}
              </Text>
            </VStack>
          </HStack>
          <Text fontSize="3xl" fontWeight="bold" color={textColor}>
            {value}
          </Text>
        </Flex>
        <Collapse in={isOpen} animateOpacity>
          <Box
            mt={4}
            p={4}
            bg={useColorModeValue("gray.50", "gray.600")}
            borderRadius="md"
          >
            <Text fontSize="sm" color={subTextColor}>
              {/* Replace this with dynamic content as needed */}
              Detailed information about {title.toLowerCase()} goes here.
            </Text>
          </Box>
        </Collapse>
      </Box>
    </SlideFade>
  );
};

// Reusable SalesSummary Component
const SalesSummary = ({ monthlyChange, metricBorderColor,merchant,storeId }) => {
  // Determine the trend direction and color
  const isIncrease = monthlyChange >= 0;
  const trendIcon = isIncrease ? FiArrowUp : FiArrowDown;
  const trendColor = isIncrease ? "green.400" : "red.400";

  // Chakra UI color modes
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");

  const [selectedFilter, setSelectedFilter] = useState("allTime");
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment("2023-01-01"), // Start date is 1st January 2000
    endDate: moment(), // End date is the current date
  });
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("user"));

  const handleSelection = (selection) => {
    setSelectedDateRange(selection);
  };
  const fetchData = async () => {
    const token = localStorage.getItem("access_token");
    const params1 = {
      startDate:moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
      endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
      merchantId:merchant?.id,
      store:storeId
    };
    try {
      const res = await API.get("/analytics/sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params1,
      });
      const data = res.data.data;
      console.log("date time tax",data, parseFloat(user?.tax)+data.tips)
      setKeyMetrics([
        {
          id: "netSales",
          title: "Net Sales",
          subTitle: "Total net revenue",
          value: parseFloat(data.grossSales-(parseFloat(user?.tax)+data.tips)).toFixed(2),
          icon: FiTrendingUp,
          color: "green.500",
        },
        {
          id: "grossSales",
          title: "Gross Sales",
          subTitle: "Total gross revenue",
          value: parseFloat(data.grossSales).toFixed(2),
          icon: FiTrendingDown,
          color: "red.500",
        },
        {
          id: "refunds",
          title: "Refunds",
          subTitle: "Total refunds issued",
          value: parseFloat(data.refunds).toFixed(2),
          icon: FiAlertCircle,
          color: "purple.500",
        },
        {
          id: "tips",
          title: "Tips",
          subTitle: "Total Tips",
          value: parseFloat(data.tips).toFixed(2),
          icon: FiGift,
          color: "purple.500",
        },
        {
          id: "tax",
          title: "Tax",
          subTitle: "Total Tax",
          value: parseFloat(data.netSales * user?.tax).toFixed(2),
          icon: FiPercent,
          color: "blue.500",
        },
      ]);

      // Set total sales from data
      setTotalSales(parseFloat(data.netSales));
    } catch (e) {
      console.log("sales data error", e);
    } finally{
      setIsLoading(false)
    }
  };
  const MotionBox = motion(Box);
  useEffect(() => {
    fetchData();
  }, [selectedDateRange,merchant,storeId]);
  return (
    <Box
      p={6}
      bg={useColorModeValue("gray.100", "gray.700")}
      borderRadius="md"
      boxShadow="md"
      mb={6}
    >
      <VStack align="start" spacing={0}>
        <HStack gap={2}>
          <Text fontSize="xl" color={textColor}>
            Total Sales
          </Text>
          <Box backgroundColor={"white"}>
            <DateSelector onDateSelect={handleSelection} />
          </Box>
        </HStack>
        <Text fontSize="sm" color={subTextColor} mb={"4"}>
          Detailed insights into your sales performance.
        </Text>
        <HStack>
          <Text fontSize="4xl" fontWeight="bold" color={textColor}>
            $
            {totalSales.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
          <Flex align="center">
            <Icon as={trendIcon} color={trendColor} w={5} h={5} />
            <Text ml={1} fontSize="md" color={trendColor} fontWeight="semibold">
              {Math.abs(monthlyChange)}% {isIncrease ? "↑" : "↓"} this month
            </Text>
          </Flex>
        </HStack>
        <Box paddingBottom={{ base: 5, md: 6, lg: 8 }}>
      {/* Header Section */}
      {isLoading ? (
        <Skeleton height="2em" mb={{ base: 3, md: 4 }} />
      ) : (
        <MotionBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Text
            fontSize={{ base: "2xl", md: "2.5xl", lg: "3xl" }} // Enhanced responsive font sizes
            fontWeight="bold"
            mb={{ base: 4, md: 5, lg: 6 }}
          >
            Key Metrics
          </Text>
        </MotionBox>
      )}

      {/* Metrics Grid */}
      {isLoading ? (
        // Display Skeleton Loaders while data is loading
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 4, lg: 5 }}
          spacing={{ base: 4, sm: 6, md: 8 }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Box
              key={index}
              borderWidth="1px"
              borderColor={metricBorderColor}
              borderRadius="md"
              p={{ base: 2, sm: 3, md: 4 }}
              boxShadow="sm"
            >
              <HStack spacing={{ base: 1, sm: 2 }} align="center" mb={{ base: 2, sm: 3 }}>
                <Skeleton circle height="30px" width="30px" />
                <Skeleton height="20px" width="100px" />
              </HStack>
              <Skeleton height="24px" width="80%" />
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        // Display actual metrics with animations once data is loaded
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 4, lg: 5 }} // Adjusted for better responsiveness
          spacing={{ base: 4, sm: 6, md: 8 }}
        >
          {keyMetrics.map((metric) => (
            <MotionBox
              key={metric.id}
              borderWidth="1px"
              borderColor={metricBorderColor}
              borderRadius="md"
              p={{ base: 2, sm: 3, md: 4 }}
              boxShadow="sm"
              transition="all 0.2s"
              maxW="100%"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
              }} // Optional hover effect for interactivity
            >
              <HStack
                spacing={{ base: 1, sm: 2, md: 3 }}
                align={{ base: "start", md: "center" }}
                mb={{ base: 2, sm: 3 }}
              >
                <Icon
                  as={metric.icon}
                  w={{ base: 3, sm: 4, md: 5, lg: 6 }} // Enhanced responsive icon sizes
                  h={{ base: 3, sm: 4, md: 5, lg: 6 }}
                  color={metric.color}
                />
                <Text
                  fontWeight="bold"
                  fontSize={{ base: "0.9rem", sm: "1rem", md: "1rem", lg: "1.1rem" }} // Enhanced responsive font sizes
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {metric.title}
                </Text>
              </HStack>
              <Text
                fontSize={{ base: "1rem", sm: "1.2rem", md: "1.3rem", lg: "1.4rem" }} // Enhanced responsive font sizes
                fontWeight="bold"
                mt={{ base: 1, sm: 2, md: 3, lg: 4 }}
              >
                $
                {parseFloat(metric.value).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            </MotionBox>
          ))}
        </SimpleGrid>
      )}
    </Box>
      </VStack>
    </Box>
  );
};

// Reusable SalesRankingTable Component
const SalesRankingTable = ({ data }) => {
  // Chakra UI color modes
  const bgColor = useColorModeValue("white", "gray.800");
  const tableBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default items per page

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null, // 'name' or 'unitsSold'
    direction: "asc", // 'asc' or 'desc'
  });

  // Handler functions for pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Handler function for sorting
  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
    setCurrentPage(1); // Reset to first page on sort
  };

  // Sort data based on sortConfig
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  // Calculate total items and total pages
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Memoize paginated data for performance optimization
  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return sortedData.slice(startIdx, endIdx);
  }, [sortedData, currentPage, itemsPerPage]);

  return (
    <Box
      p={4}
      bg={bgColor}
      borderRadius="md"
      boxShadow="md"
      mt={6}
      pl="0"
      w="full"
    >
      <VStack align="start" spacing={4}>
        {/* Header Section */}
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Sales Ranking
          </Text>
          <Text fontSize="sm" color={subTextColor}>
            Items ranked by the number of units sold.
          </Text>
        </Box>

        {/* Table Section */}
        <TableContainer w="full">
          <Table variant="simple" w="full">
            <Thead>
              <Tr>
                <Th color={textColor}>Rank</Th>
                <Th
                  color={textColor}
                  cursor="pointer"
                  onClick={() => handleSort("name")}
                >
                  <HStack spacing={1}>
                    <Text>Item</Text>
                    {
                      sortConfig.direction === "asc" && sortConfig.key === "name" ? (
                        <TriangleUpIcon w={4} h={4} />
                      ) : (
                        <TriangleDownIcon w={4} h={4} />
                      )
                    }
                  </HStack>
                </Th>
                <Th
                  color={textColor}
                  isNumeric
                  cursor="pointer"
                  onClick={() => handleSort("unitsSold")}
                >
                  <HStack spacing={1} justifyContent="flex-end">
                    <Text>Units Sold</Text>
                    {
                      sortConfig.direction === "asc" && sortConfig.key === "unitsSold" ? (
                        <TriangleUpIcon w={4} h={4} />
                      ) : (
                        <TriangleDownIcon w={4} h={4} />
                      )
                    }
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedData.map((item, index) => (
                <Tr key={item.id} bg={tableBg} borderBottomColor={borderColor}>
                  <Td
                    color={textColor}
                    fontWeight={
                      (currentPage - 1) * itemsPerPage + index === 0
                        ? "bold"
                        : "normal"
                    }
                  >
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </Td>
                  <Td
                    color={textColor}
                    fontWeight={
                      (currentPage - 1) * itemsPerPage + index === 0
                        ? "bold"
                        : "normal"
                    }
                  >
                    {item.name}
                  </Td>
                  <Td
                    color={textColor}
                    isNumeric
                    fontWeight={
                      (currentPage - 1) * itemsPerPage + index === 0
                        ? "bold"
                        : "normal"
                    }
                  >
                    {item.unitsSold}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box w="full" mt={4}>
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalPages={totalPages}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={handlePageChange}
          />
        </Box>
      </VStack>
    </Box>
  );
};

// Sales Detailed View Component
const SalesDetailedView = () => {
  const [salesData, setSalesData] = useState(null);
  const [loadingSales, setLoadingSales] = useState(true);
  const [monthlyChange, setMonthlyChange] = useState(0);
  const [salesRankingData, setSalesRankingData] = useState([]);
  const [itemRankingData, setItemRankingData] = useState({items:[]})

  const [salesAllData, setSalesAllData] = useState(null);
  const [isFilter, setIsFilter] = useState(false);
  const [merchant, setMerchant] = useState(null);
  const [storeId, setStoreid] = useState("");

  const navigate = useNavigate(); // Initialize useNavigate

  // Chakra UI color modes
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");
  const metricBorderColor = useColorModeValue("gray.300", "gray.600");
  const chartbg = useColorModeValue("gray.100", "gray.700");
  const [chartType, setChartType] = useState("line");
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment("2023-01-01"), // Start date is 1st January 2000
    endDate: moment(), // End date is the current date
  });
  const [selectedFilter, setSelectedFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const barColor = useColorModeValue("black", "lightgrey");

  const fetchData = async () => {
    const token = localStorage.getItem("access_token");
    const params1 = {
      filter: selectedFilter,
      merchantId:merchant?.id,
      store:storeId
    };
    const params2 = {
      startDate:moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
      endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
      merchantId:merchant?.id,
      store:storeId
    };
    try {
      setIsLoading(true);
      const res = await API.get("/analytics/sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: isFilter ? params1 : params2,
      });
      const resp = await API.get("/analytics/item-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: isFilter ? params1 : params2,
      });
      const itemsRes = resp.data.data;
      setItemRankingData(itemsRes)
      const transformedMonthlyData = res.data.data.salesData.map((item) => ({
        date: item.period, // Use `period` as the X-axis label
        sales: parseFloat(item.amount).toFixed(2),
        YLabelsales: item.amount,
        orderCount: item.orderCount, // Include order count if needed for additional data points
        averageOrderValue: parseFloat(item.averageOrderValue).toFixed(2), // Include average order value for additional insights
      }));
      setSalesAllData(res.data.data);
      setSalesData(transformedMonthlyData);
      setLoadingSales(false);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("sales data error", e);
    }
  };

  useEffect(() => {
    fetchData();
    // fetchAnalyticsData()
  }, [selectedFilter, selectedDateRange, merchant, storeId]);

  // Fetch sales data for the past 5 months
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const salesDataByMonth = [];
        const promises = [];

        const today = new Date();
        const monthsToFetch = 5;

        for (let i = monthsToFetch - 1; i >= 0; i--) {
          const monthDate = new Date(
            today.getFullYear(),
            today.getMonth() - i,
            1
          );
          const startDate = new Date(
            monthDate.getFullYear(),
            monthDate.getMonth(),
            1
          );
          const endDate = new Date(
            monthDate.getFullYear(),
            monthDate.getMonth() + 1,
            0
          );

          // Format dates as YYYY-MM-DD
          const startDateStr = startDate.toISOString().split("T")[0];
          const endDateStr = endDate.toISOString().split("T")[0];

          // Prepare API call
          const promise = API.get("/analytics/sales", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              startDate: startDateStr,
              endDate: endDateStr,
              store:storeId
            },
          });

          promises.push(promise);
        }

        // Execute all API calls in parallel
        const responses = await Promise.all(promises);

        responses.forEach((response) => {
          const netSales = parseFloat(response.data.data.netSales);
          salesDataByMonth.push(netSales);
        });
        // Calculate total sales
        const total = salesDataByMonth.reduce((acc, curr) => acc + curr, 0);

        // Calculate monthly change
        if (salesDataByMonth.length >= 2) {
          const lastMonthSales = salesDataByMonth[salesDataByMonth.length - 1];
          const prevMonthSales = salesDataByMonth[salesDataByMonth.length - 2];
          const change =
            prevMonthSales !== 0
              ? (
                  ((lastMonthSales - prevMonthSales) / prevMonthSales) *
                  100
                ).toFixed(2)
              : 0;
          setMonthlyChange(change);
        } else {
          setMonthlyChange(0);
        }
      } catch (error) {
        console.error("Error fetching sales data:", error);
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSalesData();
  }, [barColor,storeId]);
  const user = JSON.parse(localStorage.getItem("user"));
  const addCommasInPrices=(value)=>{
    return parseFloat(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  // const fetchAnalyticsData = async () => {
  //   try {
  //     const params1 = {
  //       filter: selectedFilter,
  //       merchantId:merchant.id,
  //       store:storeId
  //     };
  //     const params2 = {
  //       startDate: selectedDateRange.startDate?.format("YYYY-MM-DD"),
  //       endDate: selectedDateRange.endDate?.format("YYYY-MM-DD"),
  //       merchantId:merchant.id,
  //       store:storeId
  //     };
  //     const token = localStorage.getItem("access_token");
  //     const res = await API.get("/analytics/item-sales", {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //       params: isFilter ? params1 : params2,
  //     });
  //     const itemsRes = res.data.data;
  //     setItemRankingData(itemsRes)
  //      console.log("items data",itemRankingData,itemsRes)
  //   } catch (error) {
  //     console.error("Error fetching analytics data:", error);
  //   }
  // };

  // Set sales ranking data from itemRankingData
  useEffect(() => {
    if (itemRankingData && itemRankingData.items) {
      // Sort items by timesSold in descending order
      const sortedItems = [...itemRankingData.items].sort(
        (a, b) => b.totalTimesSold - a.totalTimesSold
      );
      // Map to expected format
      const rankingData = sortedItems.map((item, index) => ({
        id: index + 1,
        name: item.item,
        unitsSold: item.totalTimesSold,
      }));
      setSalesRankingData(rankingData);
    }
  }, [itemRankingData,storeId]);
  const handleSelection = (selection) => {
    console.log("date range slection",selection)
    setSelectedDateRange(selection)
    // setSelectedFilter(selection);
    // setIsFilter(true);
  };
  return (
    <Box p={5} color={textColor}>
      {loadingSales ? (
        <Flex justify="center" align="center" minH="100vh">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Heading with Back Button */}
          <Flex justify="flex-start" align="center">
            <IconButton
              icon={<FiArrowLeft />}
              variant="outline"
              color={textColor}
              borderRadius={"full"}
              onClick={() => navigate(-1)} // Navigate back in history
              //borderColor="black"
              _hover={{ bg: "gray.200" }}
              aria-label="Go Back to Analytics Dashboard"
              mr={"4"}
              size="lg"
            ></IconButton>
            <VStack align="start" spacing={1} width={"100%"}>
              <HStack display={"flex"} justifyContent={"space-between"} width={"100%"}>
                 <Heading size="xl" fontWeight="bold">
                Sales
              </Heading>
              <HStack>{
                  user.role === "admin" && 
                  <HStack><Text>By Merchants</Text><CustomersDropDown onOptionSelect={(merch)=>setMerchant(merch)}/></HStack>
                }
                  <HStack><Text>By Store</Text><StoresDropDown onOptionSelect={(store)=>{
                    setStoreid(store.id)
                    console.log("id",store)
                    }} merchantId={merchant?.id} role={user?.role}/></HStack>
                    </HStack>
                
              </HStack>
             
              <Text fontSize="md" color={subTextColor}>
                A summary of your revenue and sales by channel.
              </Text>
            </VStack>
            {/* Placeholder to balance the flex layout */}
            <Box width="75px" />
          </Flex>

          {/* Sales Summary Header */}
          <SalesSummary
            monthlyChange={monthlyChange}
            metricBorderColor={metricBorderColor}
            merchant={merchant}
            storeId={storeId}
          />

          {/* Sales Chart */}
          <Box
            // p={4}
            borderRadius="md"
            // height="450px"
            width={"full"}
            bg={chartbg}
            boxShadow="md"
          >
            <VStack>
              <HStack
                padding={4}
                width={"100%"}
                justifyContent={"flex-start"}
                wrap={"wrap"}
              >
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={(range) => {
                    setIsFilter(false);
                    setSelectedDateRange(range);
                  }}
                />
                <DateSelector onDateSelect={handleSelection} />
                <ChartSelection onChange={(type) => setChartType(type)} />
              </HStack>
              <HStack
                paddingX={4}
                width={"100%"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                gap={10}
                wrap={"wrap"}
              >
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingUp}
                    w={5}
                    h={5}
                    color={"green.500"}
                    marginRight={"4px"}
                  />
                  Net :<Text paddingLeft={2} as={"span"}>${addCommasInPrices(parseFloat(salesAllData?.grossSales-(parseFloat(user?.tax)+salesAllData?.tips)).toFixed(2))}</Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingDown}
                    w={5}
                    h={5}
                    color={"red.500"}
                    marginRight={"4px"}
                  />
                  Gross :<Text paddingLeft={2} as={"span"}>${addCommasInPrices(salesAllData?.grossSales)}</Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiAlertCircle}
                    w={5}
                    h={5}
                    color={"purple.500"}
                    marginRight={"4px"}
                  />
                  Refunds :<Text paddingLeft={2} as={"span"}>${addCommasInPrices(salesAllData?.refunds)}</Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiCheckSquare}
                    w={5}
                    h={5}
                    color={"green.500"}
                    marginRight={"4px"}
                  />
                  Order Count :
                  <Text paddingLeft={2} as={"span"}>{salesAllData?.orderCount}</Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiGift}
                    w={5}
                    h={5}
                    color={"purple.500"}
                    marginRight={"4px"}
                  />
                  Tips :<Text paddingLeft={2} as={"span"}>${addCommasInPrices(salesAllData?.tips)}</Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiPercent}
                    w={5}
                    h={5}
                    color={"blue.500"}
                    marginRight={"4px"}
                  />
                  Total Tax :
                  <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(parseFloat(salesAllData?.netSales * user?.tax).toFixed(2))}
                  </Text>
                </Text>
              </HStack>
              <Box width={"100%"} height={"380px"}>
                <BarLineChart
                  data={salesData}
                  chartType={chartType}
                  xKey={"date"}
                  yKey={"YLabelsales"}
                  valueKey={"sales"}
                  isLoading={isLoading}
                />
              </Box>
            </VStack>
          </Box>

          {/* Additional Metrics: Total Users and Total Orders */}
          {/* <Box>
            <Text fontSize="2xl" fontWeight="bold" mb={4}>
              Additional Metrics
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <InfoTile
                title="Total Users"
                subTitle="Number of active users"
                value="0"
                icon={FiUsers}
              />

              <InfoTile
                title="Total Orders"
                subTitle="Number of orders placed"
                value="0"
                icon={FiShoppingCart}
              />
            </SimpleGrid>
          </Box> */}

          {/* Sales Ranking Table */}
          <SalesRankingTable data={salesRankingData} />
        </VStack>
      )}
    </Box>
  );
};

export default SalesDetailedView;
